import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import {
  isPricingGroupBy,
  PricingGroupBy,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-grouping';
import { useCallback, useMemo } from 'react';
import { useColumnPreferencesStore } from '@/domain/configuration/blocs/stores/column-preferences-store';

// The grouping each table shipped with; a table with no stored preference
// keeps behaving the way it always has.
const DEFAULT_GROUP_BY: Partial<Record<TableName, PricingGroupBy>> = {
  [TableName.PricingAgent]: PricingGroupBy.Restaurant,
  [TableName.PricingBookmarks]: PricingGroupBy.VariantPack,
  [TableName.PricingReview]: PricingGroupBy.VariantPack,
};

export const defaultPricingGroupBy = (table: TableName): PricingGroupBy =>
  DEFAULT_GROUP_BY[table] ?? PricingGroupBy.VariantPack;

interface UsePricingGroupByResult {
  groupBy: PricingGroupBy;
  setGroupBy: (groupBy: PricingGroupBy) => void;
}

export const usePricingGroupBy = (
  table: TableName,
): UsePricingGroupByResult => {
  const stored = useColumnPreferencesStore(
    (state) => state.preferences[ConfigurationDomain.Pricing][table]?.groupBy,
  );
  const persist = useColumnPreferencesStore((state) => state.setGroupBy);

  const groupBy = isPricingGroupBy(stored)
    ? stored
    : defaultPricingGroupBy(table);

  const setGroupBy = useCallback(
    (next: PricingGroupBy) => persist(ConfigurationDomain.Pricing, table, next),
    [persist, table],
  );

  return useMemo(() => ({ groupBy, setGroupBy }), [groupBy, setGroupBy]);
};
